import React from 'react';
import { getMonthName } from '@/config/gameConstants';
import type { GameDate } from '@/types/events';
import type { NewsStory } from './useNewspaperEvents';

interface NewspaperFrontPageProps {
  story: NewsStory | null;
  companyName: string;
  gameDate: GameDate;
  hasMorePages: boolean;
}

const CATEGORY_COLORS: Record<string, string> = {
  economic: 'bg-amber-100 text-amber-800 border-amber-200',
  technology: 'bg-blue-100 text-blue-800 border-blue-200',
  competition: 'bg-red-100 text-red-800 border-red-200',
  regulation: 'bg-purple-100 text-purple-800 border-purple-200',
  social: 'bg-green-100 text-green-800 border-green-200',
  press: 'bg-cyan-100 text-cyan-800 border-cyan-200',
  development: 'bg-orange-100 text-orange-800 border-orange-200',
};

function CategoryBadge({ category }: { category: string }) {
  const colors = CATEGORY_COLORS[category] ?? 'bg-gray-100 text-gray-700 border-gray-200';
  return (
    <span
      className={`inline-block px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider
        rounded border ${colors}`}
    >
      {category}
    </span>
  );
}

export const NewspaperFrontPage: React.FC<NewspaperFrontPageProps> = ({
  story,
  companyName,
  gameDate,
  hasMorePages,
}) => {
  const dateStr = `${getMonthName(gameDate.month)} ${gameDate.day}, ${gameDate.year}`;

  return (
    <div className="flex flex-col h-full px-8 py-6 overflow-hidden">
      {/* Masthead */}
      <div className="text-center mb-1">
        <h2 className="text-2xl md:text-3xl font-display font-bold tracking-[0.15em] uppercase text-amber-950">
          {companyName || 'Company'} Times
        </h2>
        <div className="flex items-center justify-center gap-3 mt-1 text-xs text-amber-800/60">
          <span>{dateStr}</span>
          <span className="w-1 h-1 rounded-full bg-amber-800/30" />
          <span>Vol. {gameDate.year - 1999}, No. {gameDate.month}</span>
        </div>
      </div>

      {/* Masthead rule */}
      <div className="border-t-2 border-b border-amber-900/30 my-3 py-[1px]">
        <div className="border-t border-amber-900/15" />
      </div>

      {/* Content */}
      {story ? (
        <div className="flex-1 flex flex-col min-h-0">
          <div className="mb-2">
            <CategoryBadge category={story.category} />
          </div>

          <h3 className="text-xl md:text-2xl font-display font-bold leading-tight text-amber-950 mb-3">
            {story.headline}
          </h3>

          <p className="text-sm leading-relaxed text-amber-900/80 line-clamp-4 flex-1">
            {story.body}
          </p>

          {/* Story date */}
          <div className="mt-3 text-[10px] text-amber-800/50 uppercase tracking-wider">
            {getMonthName(story.gameDate.month)} {story.gameDate.day}, {story.gameDate.year}
          </div>

          {hasMorePages && (
            <p className="mt-4 text-[11px] italic text-amber-800/50 text-right">
              Continued on page 2...
            </p>
          )}
        </div>
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center text-center">
          <div className="text-4xl mb-3 opacity-30">📰</div>
          <p className="text-sm font-display text-amber-900/50">No news to report</p>
          <p className="text-xs text-amber-800/40 mt-1">
            Events will appear here as your company grows
          </p>
        </div>
      )}

      {/* Bottom decorative rule */}
      <div className="border-t border-amber-900/15 mt-auto pt-2">
        <div className="text-[8px] text-center text-amber-800/30 tracking-widest uppercase">
          Page 1
        </div>
      </div>
    </div>
  );
};

export default NewspaperFrontPage;
